import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SchemaInfo from './SchemaInfo';
import SearchResults from './SearchResults';
import './Message.css';

const AFFORDABILITY_HEAD = '**Affordability Assessment**';
const FINANCIAL_HEAD = '**Financial Considerations**';

function parseAssistantContent(content) {
  if (!content || typeof content !== 'string') return [{ type: 'text', content: content || '' }];
  const parts = [];
  let rest = content;
  const cards = [
    { key: 'affordability', title: 'Affordability Assessment', head: AFFORDABILITY_HEAD, next: '\n3. **Financial' },
    { key: 'financial', title: 'Financial Considerations', head: FINANCIAL_HEAD, next: '\n4. **Market' },
  ];
  for (const card of cards) {
    const i = rest.indexOf(card.head);
    if (i === -1) continue;
    const before = rest.slice(0, i).trim();
    if (before) parts.push({ type: 'text', content: before });
    rest = rest.slice(i + card.head.length);
    const nextI = rest.indexOf(card.next);
    const block = nextI === -1 ? rest : rest.slice(0, nextI);
    rest = nextI === -1 ? '' : rest.slice(nextI);
    const body = block.replace(/^\s*\n+/, '').trim();
    if (body) parts.push({ type: 'card', key: card.key, title: card.title, content: body });
  }
  if (rest.trim()) parts.push({ type: 'text', content: rest.trim() });
  if (parts.length === 0) parts.push({ type: 'text', content });
  return parts;
}

function MessageBody({ content, isError, isAssistant }) {
  if (!content) return null;
  if (!isAssistant || isError) {
    return <div className={`message-text ${isError ? 'error' : ''}`}>{content}</div>;
  }
  const parts = parseAssistantContent(content);
  return (
    <div className="message-text">
      {parts.map((part, i) =>
        part.type === 'text' ? (
          <div key={i} className="message-text-block">{part.content}</div>
        ) : (
          <div key={i} className={`message-card message-card-${part.key}`}>
            <h4 className="message-card-title">{part.title}</h4>
            <div className="message-card-body">{part.content}</div>
          </div>
        )
      )}
    </div>
  );
}

const Message = ({ message }) => {
  const [inferenceOpen, setInferenceOpen] = useState(false);
  const { user } = useAuth();
  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const inference = message.inference;
  // Property cards are unreadable squeezed into a 70%-wide bubble on a phone,
  // so a message carrying results gets the full row.
  const hasResults = !!message.searchResults && message.sender === 'assistant';

  // Build user initials as fallback
  const userInitials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className={`message ${message.sender}`}>
      <div className="message-avatar">
        {message.sender === 'user' ? (
          user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name || 'User'}
              className="message-avatar-img"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="message-avatar-initials">{userInitials}</span>
          )
        ) : (
          '🤖'
        )}
      </div>
      
      <div className={`message-content${hasResults ? ' has-results' : ''}`}>
        <MessageBody
          content={message.content}
          isError={message.isError}
          isAssistant={message.sender === 'assistant'}
        />
        
        {message.userSchema && message.sender === 'assistant' && !message.isError && (
          <SchemaInfo userSchema={message.userSchema} />
        )}
        
        {message.searchResults && message.sender === 'assistant' && (
          <SearchResults
            searchResults={message.searchResults}
            onFavoriteCompound={message.onFavoriteCompound}
          />
        )}

        {message.agentError && message.sender === 'assistant' && (
          <div className="message-agent-error" role="alert">
            <strong>Why fallback mode:</strong> {message.agentError}
          </div>
        )}
        {inference && message.sender === 'assistant' && (
          <div className="inference-debug">
            <button
              type="button"
              className="inference-debug-toggle"
              onClick={() => setInferenceOpen(!inferenceOpen)}
              aria-expanded={inferenceOpen}
            >
              {inferenceOpen ? '▼' : '▶'} Inference details
            </button>
            {inferenceOpen && (
              <div className="inference-debug-content">
                {(inference.execution_trace && inference.execution_trace.some(s => s.prompt)) ? (
                  <section>
                    <h5>Prompts (all steps — planning + each agent turn)</h5>
                    {(inference.execution_trace || []).map((step, i) => (
                      step.prompt ? (
                        <details key={i} className="inference-prompt-by-step">
                          <summary>Step {step.step_index} — {step.node}</summary>
                          <pre className="inference-system-prompt">{step.prompt}</pre>
                        </details>
                      ) : null
                    ))}
                  </section>
                ) : inference.system_prompt ? (
                  <section>
                    <h5>System prompt (last agent turn)</h5>
                    <pre className="inference-system-prompt">{inference.system_prompt}</pre>
                  </section>
                ) : null}
                <section>
                  <h5>Prompt context (messages sent to model)</h5>
                  <div className="inference-messages">
                    {(inference.messages_preview || []).map((m, i) => (
                      <div key={i} className={`inference-msg inference-msg-${m.role}`}>
                        <span className="inference-msg-role">{m.role}</span>
                        <pre className="inference-msg-preview">{m.content_preview || '(empty)'}</pre>
                        {m.content_length > 0 && (
                          <span className="inference-msg-len">({m.content_length} chars)</span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
                {(inference.execution_trace && inference.execution_trace.length > 0) && (
                  <section>
                    <h5>Full graph execution (calls + prompts)</h5>
                    <ol className="inference-execution-trace">
                      {inference.execution_trace.map((step, i) => (
                        <li key={i} className="inference-execution-step">
                          <div className="inference-execution-step-header">
                            <span className="inference-execution-step-index">{step.step_index}.</span>
                            <span className="inference-execution-step-node">{step.node}</span>
                          </div>
                          {step.prompt && (
                            <details className="inference-execution-details">
                              <summary>Prompt</summary>
                              <pre className="inference-execution-prompt">{step.prompt}</pre>
                            </details>
                          )}
                          <details className="inference-execution-details" open={!step.prompt}>
                            <summary>Output</summary>
                            <pre className="inference-execution-output">{step.output || '(none)'}</pre>
                          </details>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
                {(inference.loop_steps && inference.loop_steps.length > 0) && (
                  <section>
                    <h5>Graph loop (summary)</h5>
                    <ol className="inference-loop-steps">
                      {inference.loop_steps.map((step, i) => (
                        <li key={i} className="inference-loop-step">
                          <span className="inference-loop-step-index">{step.step_index}.</span>
                          <span className="inference-loop-step-node">{step.node}</span>
                          <pre className="inference-loop-step-summary">{step.summary || '(no summary)'}</pre>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
                {(inference.tool_calls && inference.tool_calls.length > 0) && (
                  <section>
                    <h5>Tool calls</h5>
                    <ul className="inference-tool-calls">
                      {inference.tool_calls.map((tc, i) => (
                        <li key={i}>
                          <code>{tc.name}</code>
                          <pre>{JSON.stringify(tc.args, null, 2)}</pre>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                <section>
                  <h5>Returned to client</h5>
                  <p className="inference-meta">
                    schema_complete: {String(inference.schema_complete)}
                    {inference.search_results_count != null && (
                      <> · search_results_count: {inference.search_results_count}</>
                    )}
                  </p>
                  <pre className="inference-assistant-returned">
                    {inference.assistant_message_returned || '(empty)'}
                  </pre>
                </section>
              </div>
            )}
          </div>
        )}
        
        <div className="message-time">
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
};

export default Message;
